import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { CheckCircle2, Loader2, X, Calculator, BadgeCheck, Undo2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function HousekeepingEditModal({ request, onClose, onSaved }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [r, setR] = useState(request);
  const [saving, setSaving] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [fxRate, setFxRate] = useState(request?.fx_rate_at_calculate || null);
  const [marking, setMarking] = useState(null); // line key currently being marked
  const set = (k, v) => setR((prev) => ({ ...prev, [k]: v }));

  const lines = [
    { key: "check_in", eurField: "cost_check_in_clean", gbpField: "gbp_paid_check_in", paidField: "paid_at_check_in", label: "Check-in clean" },
    { key: "check_out", eurField: "cost_check_out_clean", gbpField: "gbp_paid_check_out", paidField: "paid_at_check_out", label: "Check-out clean" },
    { key: "laundry", eurField: "cost_laundry", gbpField: "gbp_paid_laundry", paidField: "paid_at_laundry", label: "Laundry" },
  ];

  const overallStatus = (() => {
    const applicable = lines.filter((l) => Number(r?.[l.eurField] || 0) > 0);
    if (applicable.length === 0) return null;
    const paidCount = applicable.filter((l) => r?.[l.paidField]).length;
    if (paidCount === 0) return { label: "Unpaid", cls: "bg-[#FBE9E7] text-[#A84C30] border-[#F2C9B6]" };
    if (paidCount < applicable.length) return { label: "Part paid", cls: "bg-[#FFF4E0] text-[#C25E3E] border-[#F2D8B6]" };
    return { label: "Paid", cls: "bg-[#E9F1E5] text-[#3D6242] border-[#C8DBC1]" };
  })();

  const calculate = async () => {
    setCalculating(true);
    try {
      const { data } = await api.get("/fx/eur-to-gbp");
      const rate = Number(data.rate);
      setFxRate(rate);
      setR((prev) => ({
        ...prev,
        gbp_paid_check_in: Math.round((Number(prev.cost_check_in_clean || 0) * rate) * 100) / 100,
        gbp_paid_check_out: Math.round((Number(prev.cost_check_out_clean || 0) * rate) * 100) / 100,
        gbp_paid_laundry: Math.round((Number(prev.cost_laundry || 0) * rate) * 100) / 100,
        fx_rate_at_calculate: rate,
      }));
      toast.success(`Rate fetched: €1 = £${rate.toFixed(4)}`);
    } catch (e) {
      toast.error("Could not fetch FX rate");
    } finally {
      setCalculating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/operations/housekeeping/${r.booking_id}`, {
        with_family: r.with_family, with_friends: r.with_friends,
        adults_count: Number(r.adults_count) || 0,
        children_count: Number(r.children_count) || 0,
        laundry_bedding: !!r.laundry_bedding,
        laundry_towels: !!r.laundry_towels,
        laundry_mattress_protectors: !!r.laundry_mattress_protectors,
        laundry_blankets: !!r.laundry_blankets,
        laundry_other: !!r.laundry_other,
        notes_override: r.notes_override,
        cost_check_in_clean: Number(r.cost_check_in_clean) || 0,
        cost_check_out_clean: Number(r.cost_check_out_clean) || 0,
        cost_laundry: Number(r.cost_laundry) || 0,
        gbp_paid_check_in: r.gbp_paid_check_in == null ? null : Number(r.gbp_paid_check_in),
        gbp_paid_check_out: r.gbp_paid_check_out == null ? null : Number(r.gbp_paid_check_out),
        gbp_paid_laundry: r.gbp_paid_laundry == null ? null : Number(r.gbp_paid_laundry),
        fx_rate_at_calculate: fxRate,
      });
      setR(data);
      toast.success("Saved");
      onSaved && onSaved();
    } catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  const markPaid = async (line) => {
    setMarking(line.key);
    try {
      // Save any pending edits first so the GBP override sticks server-side.
      await api.patch(`/operations/housekeeping/${r.booking_id}`, {
        gbp_paid_check_in: r.gbp_paid_check_in == null ? null : Number(r.gbp_paid_check_in),
        gbp_paid_check_out: r.gbp_paid_check_out == null ? null : Number(r.gbp_paid_check_out),
        gbp_paid_laundry: r.gbp_paid_laundry == null ? null : Number(r.gbp_paid_laundry),
        fx_rate_at_calculate: fxRate,
      });
      const gbpVal = r[line.gbpField] == null ? null : Number(r[line.gbpField]);
      const { data } = await api.post(`/operations/housekeeping/${r.booking_id}/mark-paid`, {
        line: line.key,
        gbp_amount: gbpVal,
      });
      setR(data);
      toast.success(`${line.label} marked paid`);
      onSaved && onSaved();
    } catch (e) { toast.error("Could not mark paid"); }
    finally { setMarking(null); }
  };

  const unpay = async (line) => {
    setMarking(line.key);
    try {
      const { data } = await api.post(`/operations/housekeeping/${r.booking_id}/mark-paid`, {
        line: line.key,
        unpay: true,
      });
      setR(data);
      toast.success(`${line.label} marked unpaid`);
      onSaved && onSaved();
    } catch (e) { toast.error("Could not undo"); }
    finally { setMarking(null); }
  };

  const Toggle = ({ label, k, testid }) => (
    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" className="w-4 h-4 accent-[#C25E3E]" checked={!!r[k]} onChange={(e) => set(k, e.target.checked)} data-testid={testid} />
      {label}
    </label>
  );
  const inp = "w-full bg-[#FDFBF7] border border-[#E8E1D5] rounded-xl px-3 py-2 text-sm";

  return (
    <div className="fixed inset-0 z-50 bg-[#2B2B2B]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="hk-edit-modal">
      <div className="bg-[#FDFBF7] rounded-2xl border border-[#E8E1D5] max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-5 sm:p-6 border-b border-[#E8E1D5] flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Housekeeping</p>
            <h3 className="font-serif-villa text-2xl">{r.ref_number || "Edit"} · {r.booking?.title || "Stay"}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#EAE4D8]"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 sm:p-6 space-y-5 overflow-y-auto">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-2">Who's staying</div>
            <div className="flex flex-wrap gap-3">
              <Toggle label="+ Family" k="with_family" testid="hk-with-family" />
              <Toggle label="+ Friends" k="with_friends" testid="hk-with-friends" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <label className="text-xs text-[#7A7A7A]">Adults<input className={inp} type="number" min={0} value={r.adults_count || 0} onChange={(e) => set("adults_count", e.target.value)} data-testid="hk-adults" /></label>
              <label className="text-xs text-[#7A7A7A]">Children<input className={inp} type="number" min={0} value={r.children_count || 0} onChange={(e) => set("children_count", e.target.value)} data-testid="hk-children" /></label>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-2">Laundry requirements</div>
            <div className="grid grid-cols-2 gap-2">
              <Toggle label="Bedding" k="laundry_bedding" testid="hk-laundry-bedding" />
              <Toggle label="Towels" k="laundry_towels" testid="hk-laundry-towels" />
              <Toggle label="Mattress protectors" k="laundry_mattress_protectors" testid="hk-laundry-mattress_protectors" />
              <Toggle label="Blankets" k="laundry_blankets" testid="hk-laundry-blankets" />
              <Toggle label="Other (see notes)" k="laundry_other" testid="hk-laundry-other" />
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A] mb-2">Special notes</div>
            <textarea className={inp + " min-h-[100px]"} value={r.notes_override || r.notes || ""} onChange={(e) => set("notes_override", e.target.value)} data-testid="hk-notes-override" />
          </div>
          {isAdmin && (
          <div data-testid="hk-payments">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div>
                <div className="text-[11px] uppercase tracking-[0.25em] text-[#7A7A7A]">Cleaning costs (after the stay)</div>
                <div className="text-xs text-[#9A9A9A]">Cleaner bills in EUR — calculate to fill GBP at today's rate, override per line, then mark each paid.</div>
              </div>
              <div className="flex items-center gap-2">
                {overallStatus && (
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border ${overallStatus.cls}`} data-testid="hk-payment-status">{overallStatus.label}</span>
                )}
                <button
                  type="button"
                  onClick={calculate}
                  disabled={calculating}
                  data-testid="hk-fx-calculate"
                  className="px-3 py-1.5 rounded-full text-xs bg-[#FDFBF7] border border-[#E8E1D5] hover:bg-[#F5F0E6] inline-flex items-center gap-1 disabled:opacity-50"
                >
                  {calculating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Calculator className="w-3 h-3" />}
                  Calculate
                </button>
              </div>
            </div>
            {fxRate && (
              <div className="text-[11px] text-[#7A7A7A] mb-2" data-testid="hk-fx-rate">Rate used: €1 = £{Number(fxRate).toFixed(4)}</div>
            )}
            <div className="space-y-2">
              {lines.map((line) => {
                const paid = !!r[line.paidField];
                const eurVal = r[line.eurField] || 0;
                const gbpVal = r[line.gbpField] == null ? "" : r[line.gbpField];
                return (
                  <div key={line.key} className="grid grid-cols-12 gap-2 items-center border border-[#E8E1D5] rounded-xl p-3 bg-[#FBF7EF]" data-testid={`hk-line-${line.key}`}>
                    <div className="col-span-12 sm:col-span-3">
                      <div className="text-[11px] uppercase tracking-wider text-[#7A7A7A]">{line.label}</div>
                    </div>
                    <label className="col-span-6 sm:col-span-3 text-[10px] uppercase tracking-wider text-[#7A7A7A]">EUR
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]">€</span>
                        <input
                          type="number" min={0} step="0.01"
                          className={inp + " pl-6"}
                          value={eurVal}
                          onChange={(e) => set(line.eurField, e.target.value)}
                          disabled={paid}
                          data-testid={`hk-eur-${line.key}`}
                        />
                      </div>
                    </label>
                    <label className="col-span-6 sm:col-span-3 text-[10px] uppercase tracking-wider text-[#7A7A7A]">GBP paid
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7A7A7A]">£</span>
                        <input
                          type="number" min={0} step="0.01"
                          className={inp + " pl-6"}
                          value={gbpVal}
                          onChange={(e) => set(line.gbpField, e.target.value === "" ? null : e.target.value)}
                          disabled={paid}
                          placeholder="—"
                          data-testid={`hk-gbp-${line.key}`}
                        />
                      </div>
                    </label>
                    <div className="col-span-12 sm:col-span-3 flex justify-end">
                      {paid ? (
                        <button type="button" onClick={() => unpay(line)} disabled={marking === line.key} data-testid={`hk-unpay-${line.key}`} className="px-3 py-1.5 rounded-full text-xs bg-[#E9F1E5] border border-[#C8DBC1] text-[#3D6242] inline-flex items-center gap-1 hover:bg-[#DCE9D6]">
                          {marking === line.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <BadgeCheck className="w-3 h-3" />} Paid <Undo2 className="w-3 h-3 opacity-60" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => markPaid(line)}
                          disabled={marking === line.key || Number(eurVal || 0) <= 0 || gbpVal === "" || gbpVal == null}
                          data-testid={`hk-pay-${line.key}`}
                          className="px-3 py-1.5 rounded-full text-xs bg-[#C25E3E] text-[#FDFBF7] hover:bg-[#A84C30] inline-flex items-center gap-1 disabled:opacity-40"
                        >
                          {marking === line.key ? <Loader2 className="w-3 h-3 animate-spin" /> : <BadgeCheck className="w-3 h-3" />} Mark paid
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>
        <div className="p-5 sm:p-6 border-t border-[#E8E1D5] flex flex-wrap gap-2 justify-end">
          <button onClick={onClose} className="px-4 py-2 rounded-full text-sm text-[#7A7A7A] hover:text-[#2B2B2B]">Close</button>
          <button onClick={save} disabled={saving} data-testid="hk-edit-save" className="px-4 py-2 rounded-full text-sm bg-[#C25E3E] text-[#FDFBF7] hover:bg-[#A84C30] inline-flex items-center gap-2 disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
